import { lazyWithReload } from "./lazyWithReload";
import { Suspense, useState } from "react";
import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router";
import { Toaster } from "sonner";
import { ApiError } from "@/data";
import { keys } from "@/data/queries";
import { ThemeProvider, useTheme } from "./theme";
import { LanguageProvider } from "@/i18n/i18n";
import { FullScreenLoading, PublicOnly, RequireAuth, RequirePermission, RoleLanding } from "./guards";
import { APP_ROUTES } from "./routes";
import LoginPage from "@/features/auth/LoginPage";
import NotFoundPage from "@/features/system/NotFoundPage";

// The signed-in shell (menus, popovers) loads after login so the login page stays light.
const AppShell = lazyWithReload(() => import("./layout/AppShell").then((m) => ({ default: m.AppShell })));

function makeQueryClient() {
  // An expired session or a user disabled in the sheet surfaces as UNAUTHENTICATED anywhere; drop the user so guards send them to login.
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === "UNAUTHENTICATED") qc.setQueryData(keys.me, null);
  };
  const qc: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: { retry: (n, e) => !(e instanceof ApiError) && n < 2, refetchOnWindowFocus: true },
    },
  });
  return qc;
}

const routes: RouteObject[] = [
  { path: "/login", element: <PublicOnly><LoginPage /></PublicOnly> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <Suspense fallback={<FullScreenLoading />}>
          <AppShell />
        </Suspense>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <RoleLanding /> },
      ...APP_ROUTES.map(({ path, require, devOnly, Component }) => ({
        path,
        element: <RequirePermission require={require} devOnly={devOnly}><Component /></RequirePermission>,
      })),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(routes);

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
}

export function App() {
  const [qc] = useState(makeQueryClient);
  return (
    <ThemeProvider>
      <QueryClientProvider client={qc}>
        <LanguageProvider>
          <RouterProvider router={router} />
          <ThemedToaster />
        </LanguageProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
